import React, { useState } from 'react';
import Routes from 'routes';
import styled from 'styled-components';

import COLORS from 'constants/colors';
import DecafOrder from 'decaf/models/DecafOrder';
import FailedAnalysisWarningOverlay from 'shared/FailedAnalysisWarning';
import HumidityAnalysis from 'shared/HumidityAnalysis';
import Icon from 'components/elements/Icon';
import useStores from 'utils/useStores';
import PhysicalAnalysis from './PhysicalAnalysis';
import Roasting from 'shared/Roasting';
import SensorialAnalysis from './SensorialAnalysis';
import CloseDecafOrderButton from './CloseDecafOrderButton';
import { customFetch } from 'utils/fetch';

export interface DecafOrderRowProps {
  decafOrder: DecafOrder;
}

const RejectedLabel = styled.span`
  align-items: center;
  display: inline-flex;
  gap: 0.25rem;
  color: ${COLORS.RED};
  font-weight: bold;
`;

const AnalysisCell = styled.td<{ $readOnly?: boolean }>`
  ${({ $readOnly }) =>
    $readOnly &&
    `
      opacity: 0.5;
      pointer-events: none;
    `}
`;

function DecafOrderRow({ decafOrder }: DecafOrderRowProps) {
  const { qualityAssuranceStore } = useStores();
  const { ico, sample, id } = decafOrder;
  const {
    current_humidity_analysis,
    current_qa_physical_analysis,
    current_roasting,
    current_sensorial_analysis,
  } = sample;

  const handleOnHumidityAnalysisPrimaryClick = (_, values) => {
    current_humidity_analysis.synchronize(values);
  };

  const handleOnRoastingPrimaryClick = (event: React.MouseEvent, values: {}) => {
    current_roasting.synchronize(values);
  };

  const [pendingClose, setPendingClose] = useState(false);

  const closeDecafOrder = (): Promise<unknown> =>
    customFetch(Routes.close_api_v1_decaf_decaf_order_path(id)).then(() => {
      window.location.href = Routes.decaf_quality_assurance_index_path();
    });

  const handleOnCloseDecafOrderClick = (): Promise<unknown> | void => {
    if (sample.passed) return closeDecafOrder();

    setPendingClose(true);
  };

  return (
    <tr>
      <td className='table-border'>{ico.main_identifier}</td>

      <AnalysisCell className='align-center table-border' $readOnly={decafOrder.rejected}>
        <HumidityAnalysis
          identifier={ico.main_identifier}
          onPrimaryClick={handleOnHumidityAnalysisPrimaryClick}
          humidityAnalysis={current_humidity_analysis}
          lotFailed={false}
          validations={current_humidity_analysis.validations}
          text={current_humidity_analysis.text}
          status={current_humidity_analysis.status}
          icon={current_humidity_analysis.icon}
          reasons={qualityAssuranceStore.humidityAcceptingReasons}
          showConfirmation={true}
        />
      </AnalysisCell>

      <AnalysisCell className='align-center table-border' $readOnly={decafOrder.rejected}>
        <PhysicalAnalysis
          physicalAnalysis={current_qa_physical_analysis}
          newUrl={Routes.new_decaf_decaf_order_physical_analysis_path(id)}
          editUrl={Routes.edit_decaf_decaf_order_physical_analysis_path(id)}
        />
      </AnalysisCell>

      <AnalysisCell className='align-center table-border' $readOnly={decafOrder.rejected}>
        <Roasting
          identifier={ico.main_identifier}
          results={qualityAssuranceStore.roastingResults}
          roasting={current_roasting}
          roastingSampleWeight={qualityAssuranceStore.roastingSampleWeight || 110}
          text={I18n.translate(
            `widgets.lot_status_table.${current_roasting.passed ? 'done' : 'failed'}`
          )}
          status={current_roasting.passed}
          icon={current_roasting.passed ? 'passed' : 'failed'}
          onPrimaryClick={handleOnRoastingPrimaryClick}
          showReroast={false}
          showEnterRoasting={!current_roasting.id}
          validations={qualityAssuranceStore.roastingValidations}
        />
      </AnalysisCell>

      <AnalysisCell className='align-center table-border' $readOnly={decafOrder.rejected}>
        <SensorialAnalysis
          sample={sample}
          sensorialAnalysis={current_sensorial_analysis}
          cuppingSessionEnabled={qualityAssuranceStore.cuppingSessionEnabled}
        />
      </AnalysisCell>

      <td className='align-center table-border'>{`${ico.weight.value} ${ico.weight.unit}`}</td>

      <td className='align-center'>
        {decafOrder.rejected ? (
          <RejectedLabel>
            <Icon icon='cross' color={COLORS.RED} />

            {I18n.translate('decaf.quality_assurance.rejected')}
          </RejectedLabel>
        ) : (
          (sample.failed || sample.passed) && (
            <CloseDecafOrderButton
              passed={sample.passed}
              onClick={handleOnCloseDecafOrderClick}
            />
          )
        )}
      </td>
      {pendingClose && (
        <FailedAnalysisWarningOverlay
          identifier={ico.main_identifier}
          lead={I18n.translate('failed_analysis_warning.lead_close')}
          onConfirmClick={() => closeDecafOrder()}
          onCancelClick={() => setPendingClose(false)}
        />
      )}
    </tr>
  );
}

export default DecafOrderRow;
